/**
 * Grade normalization utilities for cross-gym comparison.
 * All functions are pure — no side effects or React dependencies.
 * Functions accept an optional gradeScale param; falls back to GRADE_SCALE constant.
 *
 * `getNormPoints` and `formatGradeRange` are the shared implementations from
 * `@sendly/shared` (the registration callables and the e-mails run the same
 * code); the ascent-distribution aggregation below is client-only.
 */
import { GRADE_SCALE as DEFAULT_GRADE_SCALE } from "./constants";
import { resolveAscentRoute } from "./utils/ascentRoute";
import { difficultyLevelOfGrade } from "@sendly/shared";

export { getNormPoints, formatGradeRange } from "@sendly/shared";

/**
 * Maximum gradeId from a difficulty level's gradeRange.
 * @param {object} diffLevel - A customDifficulty entry
 * @returns {number|null} gradeId of the range top, or null
 */
export function getMaxGrade(diffLevel) {
  if (!diffLevel?.gradeRange) return null;
  return diffLevel.gradeRange.max;
}

/**
 * Aggregate ascents into grade bands for distribution display.
 * Groups by bands of `bandSize` gradeIds for readability.
 *
 * The optional `spray` input adds the climber's benchmark Spraywall first
 * sends (the level side of spray, never points). A spray grade is a rung of the
 * platform grade scale, so it lands in a band by the rung itself, where a route
 * lands by the middle of its difficulty level's range. `count` / `flashCount`
 * are then the TOTALS (route + spray) and `sprayCount` / `sprayFlashCount` the
 * spray subsets, so a bar can draw the spray part as its own fainter segment
 * while the ideal pyramid and the flash rate keep reading one population.
 * Without `spray` the result is exactly the route-only one.
 *
 * @param {Array} gymDataArray - [{ ascents, routes, diff }, ...]
 * @param {string} userId - filter ascents by user
 * @param {string} preference - "font" or "v"
 * @param {number} bandSize - how many gradeIds per band (default 3)
 * @param {Array} [gradeScale] - optional custom grade scale array
 * @param {{ g?: object, fg?: object }|null} [spray] - benchmark sends per rung
 *   (`benchmarkSendsInRange` output, summed over the gyms the caller includes)
 * @returns {Array} [{ label, count, flashCount, sprayCount, sprayFlashCount, percentage, bandStart }, ...]
 */
export function aggregateGradeDistribution(gymDataArray, userId, preference = "font", bandSize = 3, gradeScale, spray = null) {
  const scale = gradeScale || DEFAULT_GRADE_SCALE;
  const bandCounts = {};
  const bandFlashCounts = {};
  const bandSprayCounts = {};
  const bandSprayFlashCounts = {};
  let total = 0;

  for (const g of gymDataArray) {
    for (const a of g.ascents) {
      if (userId && a.userId !== userId) continue;
      const r = resolveAscentRoute(a, g.routes);
      if (!r) continue;
      const d = g.diff[r.difficulty];
      if (!d?.gradeRange) continue;
      const midGradeId = Math.round((d.gradeRange.min + d.gradeRange.max) / 2);
      const bandStart = Math.floor(midGradeId / bandSize) * bandSize;
      bandCounts[bandStart] = (bandCounts[bandStart] || 0) + 1;
      if (a.flash) bandFlashCounts[bandStart] = (bandFlashCounts[bandStart] || 0) + 1;
      total++;
    }
  }

  // A rung outside the scale has no label and would be dropped below, so it
  // must not inflate `total` (and with it the percentages) either.
  const sprayG = spray && typeof spray.g === "object" && spray.g ? spray.g : {};
  const sprayFg = spray && typeof spray.fg === "object" && spray.fg ? spray.fg : {};
  for (const [rungKey, n] of Object.entries(sprayG)) {
    const rung = Number(rungKey);
    const c = Number(n);
    if (!Number.isInteger(rung) || rung < 0 || rung >= scale.length || !(c > 0)) continue;
    const bandStart = Math.floor(rung / bandSize) * bandSize;
    const f = Math.min(c, Math.max(0, Number(sprayFg[rungKey]) || 0));
    bandCounts[bandStart] = (bandCounts[bandStart] || 0) + c;
    bandSprayCounts[bandStart] = (bandSprayCounts[bandStart] || 0) + c;
    if (f > 0) {
      bandFlashCounts[bandStart] = (bandFlashCounts[bandStart] || 0) + f;
      bandSprayFlashCounts[bandStart] = (bandSprayFlashCounts[bandStart] || 0) + f;
    }
    total += c;
  }

  if (total === 0) return [];

  const field = preference === "v" ? "v" : preference === "uiaa" ? "uiaa" : preference === "french" ? "french" : "font";
  return Object.entries(bandCounts)
    .map(([startStr, count]) => {
      const start = parseInt(startStr);
      const end = Math.min(start + bandSize - 1, scale.length - 1);
      const startG = scale[start];
      const endG = scale[end];
      if (!startG || !endG) return null;
      const label = start === end ? startG[field] : `${startG[field]}–${endG[field]}`;
      return {
        label, count, flashCount: bandFlashCounts[start] || 0,
        sprayCount: bandSprayCounts[start] || 0, sprayFlashCount: bandSprayFlashCounts[start] || 0,
        percentage: Math.round((count / total) * 100), bandStart: start,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.bandStart - b.bandStart);
}

/**
 * The difficulty level whose `gradeRange { min, max }` contains a GRADE_SCALE
 * index, as `{ key, ...level }`. Ranges are per level and not exclusive, so the
 * lowest `order` wins (a level without a numeric `order` sorts last, then by
 * key). `null` for a non-object config, a non-integer index, a config with no
 * `gradeRange` at all, or no containing level.
 * @param {object} difficultyConfig - the gym's difficulty set keyed by level key
 * @param {number} gradeIdx - index into GRADE_SCALE
 * @returns {object|null}
 */
export function difficultyForGrade(difficultyConfig, gradeIdx) {
  // One "lowest order wins" rule for the whole app: the shared grade bands.
  return difficultyLevelOfGrade(difficultyConfig, gradeIdx);
}
